import { Vector3, type Camera, type WebGLRenderer } from "three";
import { DateTime } from "luxon";

import { EARTH_RADIUS } from "./constants";
import { latLngToVec3 } from "./geo";
import { CAPITALS, type CapitalDef } from "./capitals";
import { zoneColor } from "./zonecolor";
import { inZone } from "./tzrules";

export type CityDef = CapitalDef;

/** A zone's principal city from tzdb zone1970.tab — built into
 *  public/data/zone-cities.json by scripts/build-layer-data.mjs. */
export interface ZoneCityDef extends CityDef {
  country: string;
}

/** Re-exported for anything that wants the raw list. */
export const CITIES: CityDef[] = CAPITALS;

interface CityInstance {
  def: CityDef;
  /** Zone cities show only in the Time zones layer. */
  kind: "capital" | "zone";
  el: HTMLDivElement;
  timeEl: HTMLSpanElement;
  offsetEl: HTMLSpanElement;
  worldPos: Vector3;
  /** Approximate label size in CSS pixels, used for collision culling. */
  width: number;
  height: number;
}

// Layout tuning for the collision culler.
const LABEL_WIDTH = 88;       // px — generous bbox for "New Delhi" etc.
const LABEL_HEIGHT = 30;      // px — 2-line stack
const LABEL_PAD = 6;          // px — extra space between neighbours
const OFFSET_WIDTH = 34;      // px — the "UTC+5:30" tag in the Time zones layer

// A zone city this close to a capital (degrees) is the same place —
// Berlin the zone and Berlin the capital get one label.
const SAME_PLACE_DEG = 0.6;

export class CityLayer {
  private readonly root: HTMLElement;
  private readonly camera: Camera;
  private readonly renderer: WebGLRenderer;
  private readonly instances: CityInstance[];
  private readonly tmp = new Vector3();
  private readonly toCam = new Vector3();
  private zonesMode = false;

  constructor(root: HTMLElement, camera: Camera, renderer: WebGLRenderer) {
    this.root = root;
    this.camera = camera;
    this.renderer = renderer;
    this.instances = CAPITALS.map((def) => this.createInstance(def, "capital"));
  }

  /** Add the tz zones' principal cities, skipping any that coincide
   *  with a capital already on the globe. */
  addZoneCities(defs: ZoneCityDef[]): void {
    const capitals = this.instances.filter((i) => i.kind === "capital");
    for (const def of defs) {
      const dup = capitals.some(
        (c) =>
          c.def.name === def.name ||
          (Math.abs(c.def.lat - def.lat) < SAME_PLACE_DEG &&
            Math.abs(c.def.lng - def.lng) < SAME_PLACE_DEG),
      );
      if (!dup) this.instances.push(this.createInstance(def, "zone"));
    }
  }

  /** Time zones layer on/off: zone cities and UTC-offset tags. */
  setZonesMode(on: boolean): void {
    this.zonesMode = on;
    this.root.classList.toggle("city-layer--zones", on);
    for (const inst of this.instances) {
      inst.width = LABEL_WIDTH + (on ? OFFSET_WIDTH : 0);
      if (!on && inst.kind === "zone") inst.el.classList.remove("city--visible");
    }
  }

  private createInstance(def: CityDef, kind: CityInstance["kind"]): CityInstance {
    const el = document.createElement("div");
    el.className = `city city--t${def.tier} city--${kind}`;
    el.innerHTML = `
      <span class="city__name"></span>
      <span class="city__line"><span class="city__time">--:--</span><span class="city__offset"></span></span>
    `;
    (el.querySelector(".city__name") as HTMLSpanElement).textContent = def.name;
    this.root.appendChild(el);

    return {
      def,
      kind,
      el,
      timeEl: el.querySelector(".city__time") as HTMLSpanElement,
      offsetEl: el.querySelector(".city__offset") as HTMLSpanElement,
      worldPos: latLngToVec3(def.lat, def.lng, EARTH_RADIUS * 1.002),
      width: LABEL_WIDTH,
      height: LABEL_HEIGHT,
    };
  }

  /**
   * Called every frame. Projects 3D city positions to screen space, then
   * does a priority-ordered collision pass — tier-1 capitals always win,
   * tier-3 only survive where there's space.
   */
  updatePositions(sunDir: Vector3): void {
    const canvas = this.renderer.domElement;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    const camPos = this.camera.position;

    // --- Pass 1: compute visibility + screen-space position for each
    //            capital; mark day/night. Build a candidate list that
    //            the culling pass will consume. ---
    interface Candidate {
      inst: CityInstance;
      x: number;
      y: number;
      facing: number;    // higher = more head-on
    }
    const candidates: Candidate[] = [];

    for (const inst of this.instances) {
      if (inst.kind === "zone" && !this.zonesMode) continue;
      this.toCam.copy(camPos).sub(inst.worldPos).normalize();
      const facing = inst.worldPos.dot(this.toCam);
      if (facing <= 0.08) {
        inst.el.classList.remove("city--visible");
        continue;
      }

      const cosSun = inst.worldPos.clone().normalize().dot(sunDir);
      inst.el.classList.toggle("city--day", cosSun > 0.05);
      inst.el.classList.toggle("city--night", cosSun <= 0.05);

      this.tmp.copy(inst.worldPos).project(this.camera);
      const x = (this.tmp.x * 0.5 + 0.5) * w;
      const y = (-this.tmp.y * 0.5 + 0.5) * h;

      candidates.push({ inst, x, y, facing });
    }

    // --- Pass 2: sort by priority. Tier 1 first, then by how head-on the
    //            city is (faces-camera priority breaks ties). Tier-2 and
    //            tier-3 are placed only if they don't collide with an
    //            already-placed higher-priority label. ---
    candidates.sort((a, b) => {
      if (a.inst.def.tier !== b.inst.def.tier) {
        return a.inst.def.tier - b.inst.def.tier;
      }
      return b.facing - a.facing;
    });

    const placed: Candidate[] = [];
    for (const c of candidates) {
      let collides = false;
      for (const p of placed) {
        const dx = Math.abs(c.x - p.x);
        const dy = Math.abs(c.y - p.y);
        const minDx = (c.inst.width + p.inst.width) / 2 + LABEL_PAD;
        const minDy = (c.inst.height + p.inst.height) / 2 + LABEL_PAD;
        if (dx < minDx && dy < minDy) {
          collides = true;
          break;
        }
      }
      if (collides) {
        c.inst.el.classList.remove("city--visible");
        continue;
      }
      c.inst.el.style.transform = `translate3d(${c.x}px, ${c.y}px, 0) translate(-50%, -50%)`;
      c.inst.el.classList.add("city--visible");
      placed.push(c);
    }
  }

  /** Called on a slower cadence — updates the clock label. */
  updateClocks(now: DateTime): void {
    for (const inst of this.instances) {
      if (inst.kind === "zone" && !this.zonesMode) continue;
      const local = inZone(now, inst.def.tz);
      inst.timeEl.textContent = local.toFormat("HH:mm");
      if (this.zonesMode) {
        const offset = local.offset; // minutes east of UTC
        inst.offsetEl.textContent = formatOffset(offset);
        // The tag wears the zone's own pastel, as on the map.
        inst.el.style.setProperty("--zone-rgb", zoneColor(offset).join(" "));
      }
    }
  }

  dispose(): void {
    for (const inst of this.instances) {
      inst.el.remove();
    }
  }
}

/** 330 → "UTC+5:30", -180 → "UTC−3", 0 → "UTC". */
function formatOffset(minutes: number): string {
  if (minutes === 0) return "UTC";
  const sign = minutes > 0 ? "+" : "−";
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `UTC${sign}${h}${m ? `:${String(m).padStart(2, "0")}` : ""}`;
}
